/** Preserve the learner's explicit course context on runtime requests only.
 * Standalone flows intentionally remain independently accessible. This helper is
 * local to runtime feature clients; it does not alter authentication or axios.
 */
export const courseRuntimeUrl = (path: string): string => {
  if (typeof window === 'undefined' || !path.startsWith('/api/')) return path;
  const [endpoint, query = ''] = path.split('?');
  // Never attach course context to authoring, authentication, lists, or metadata.
  const isRuntime = /^\/api\/flows\/[^/]+\/(?:$|questions\/|progress\/|submit\/|mode\/|remaining-count\/|stats\/|completion-status\/|requirements-met\/|voice\/|roleplay\/|companion\/|answer-evaluation\/|question-generation\/|answer-references\/)/.test(endpoint)
    || /^\/api\/responses\/(?:\d+\/)?$/.test(endpoint)
    || /^\/api\/quiz\/(?:intro|start|submit|results)\/\d+\/$/.test(endpoint)
    || /^\/api\/quiz\/(?:attempts\/\d+\/(?:responses\/)?)$/.test(endpoint)
    || /^\/api\/questions\/\d+\/(?:tts|summary|ai-summary)\/(?:stream\/)?$/.test(endpoint);
  if (!isRuntime) return path;


  const { pathname, search } = window.location;
  const courseRoute = pathname.match(/^\/courses\/([^/]+)\/flows\/[^/]+\/?$/);
  const legacyRoute = /^\/(?:flows|quiz-results)\/[^/]+\/?$/.test(pathname);
  if (!courseRoute && !legacyRoute) return path;

  const selected = courseRoute ? [courseRoute[1]] : new URLSearchParams(search).getAll('courseId');
  if (selected.length === 0) return path;
  if (selected.some((value) => !/^[1-9]\d*$/.test(value) || !Number.isSafeInteger(Number(value)))) {
    throw new Error('Invalid course context. Return to your course and try again.');
  }
  if (selected.some((value) => Number(value) !== Number(selected[0]))) {
    throw new Error('Conflicting course context. Return to your course and try again.');
  }

  const params = new URLSearchParams(query);
  const existing = params.getAll('course_id');
  if (existing.some((value) => value !== selected[0])) {
    throw new Error('Conflicting course context. Return to your course and try again.');
  }
  params.set('course_id', selected[0]);
  return `${endpoint}?${params.toString()}`;
};


export interface CapturedCourseContext { courseId: number | null }

/** Resolve before queueing work that may finish after the learner navigates. */
export const captureCourseRuntimeContext = (): CapturedCourseContext => {
  const query = courseRuntimeUrl('/api/responses/').split('?')[1];
  const selected = new URLSearchParams(query).get('course_id');
  return { courseId: selected === null ? null : Number(selected) };
};

export const capturedCourseRuntimeUrl = (path: string, context: CapturedCourseContext): string => {
  if (context.courseId === null) return path;
  if (!Number.isSafeInteger(context.courseId) || context.courseId <= 0) throw new Error('Invalid course context.');
  return `${path}?course_id=${context.courseId}`;
};
